<template>
  <v-card
    v-if="isAnalyzing"
    class="analysis-progress-card"
    :class="{ 'is-cancelling': isCancelRequested }"
  >
    <v-card-title class="py-3">
      <v-icon left :color="isCancelRequested ? 'warning' : 'primary'">
        {{ isCancelRequested ? 'mdi-cancel' : 'mdi-clock-outline' }}
      </v-icon>
      <span>{{ isCancelRequested ? 'Cancel requested…' : 'Analysis in Progress' }}</span>
      <v-spacer />
      <v-btn
        v-if="canCancel"
        small
        text
        color="error"
        class="cancel-button"
        :loading="cancelling"
        :disabled="cancelling"
        @click="requestCancel"
      >
        <v-icon small left>mdi-close-circle-outline</v-icon>
        Cancel
      </v-btn>
    </v-card-title>

    <v-card-text class="pa-4">
      <!-- Progress bar -->
      <div class="mb-3">
        <v-progress-linear
          :value="progress"
          height="25"
          :color="isCancelRequested ? 'warning' : 'primary'"
          striped
          :buffer-value="progress + 10"
        >
          <template #default="{ value }">
            <strong class="white--text">{{ Math.ceil(value) }}%</strong>
          </template>
        </v-progress-linear>
      </div>

      <!-- Current step -->
      <div class="current-step-text">
        <v-icon small left>mdi-information-outline</v-icon>
        {{ stepText }}
      </div>

      <!-- Stages skipped in this run (engine off): progress status 'skipped' -->
      <div v-if="skippedStages.length" class="mt-2 skipped-stages" data-testid="skipped-stages">
        <v-chip
          v-for="stage in skippedStages"
          :key="stage"
          x-small
          outlined
          class="mr-1 mb-1 skipped-stage-chip"
          :data-stage="stage"
          title="Not requested for this run"
        >
          <v-icon x-small left>mdi-debug-step-over</v-icon>
          Skipped: {{ humanizeStage(stage) }}
        </v-chip>
      </div>

      <!-- Estimated time (if available) -->
      <div v-if="estimatedTimeRemaining && !isCancelRequested" class="mt-2 caption grey--text">
        <v-icon x-small left>mdi-timer-sand</v-icon>
        Estimated time remaining: {{ formatTime(estimatedTimeRemaining) }}
      </div>

      <div v-if="cancelError" class="mt-2 caption error--text cancel-error">
        {{ cancelError }}
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import AnalysisRunsService from '@/services/AnalysisRunsService';

/** Progress statuses after which nothing else will arrive for this run. */
export const TERMINAL_PROGRESS_STATUSES = Object.freeze(['completed', 'failed', 'cancelled']);

/** Lower-cased, trimmed status or '' (the worker sends 'started', 'running', 'cancel_requested', ...). */
export function normalizeProgressStatus(status) {
  return typeof status === 'string' ? status.trim().toLowerCase() : '';
}

export function isTerminalProgressStatus(status) {
  return TERMINAL_PROGRESS_STATUSES.includes(normalizeProgressStatus(status));
}

export default {
  name: 'AnalysisProgress',

  props: {
    progress: {
      type: Number,
      default: 0,
    },
    currentStep: {
      type: String,
      default: '',
    },
    isAnalyzing: {
      type: Boolean,
      default: false,
    },
    estimatedTimeRemaining: {
      type: Number,
      default: null,
    },
    /** Run status as broadcast by the worker / ledger (e.g. 'running', 'cancel_requested'). */
    status: {
      type: String,
      default: '',
    },
    /** Ledger run id; enables the Cancel button when present. */
    runId: {
      type: String,
      default: null,
    },
    /** Stage labels the worker reported with status 'skipped' (engine not requested). */
    skippedStages: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      cancelling: false,
      cancelError: null,
    };
  },

  computed: {
    normalizedStatus() {
      return normalizeProgressStatus(this.status);
    },
    isCancelRequested() {
      return this.normalizedStatus === 'cancel_requested';
    },
    canCancel() {
      return (
        Boolean(this.runId) && !this.isCancelRequested && !isTerminalProgressStatus(this.status)
      );
    },
    stepText() {
      if (this.isCancelRequested) {
        return this.currentStep
          ? `Waiting for the worker to stop (${this.currentStep})`
          : 'Waiting for the worker to stop…';
      }
      return this.currentStep || 'Starting analysis...';
    },
  },

  watch: {
    runId() {
      this.cancelError = null;
    },
  },

  methods: {
    humanizeStage(stage) {
      return String(stage || '')
        .toLowerCase()
        .replace(/_/g, ' ');
    },

    formatTime(seconds) {
      if (!seconds || seconds <= 0) return '...';

      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);

      if (mins > 0) {
        return `${mins} min ${secs} sec`;
      }
      return `${secs} sec`;
    },

    async requestCancel() {
      if (!this.runId || this.cancelling) return;
      this.cancelling = true;
      this.cancelError = null;
      try {
        await AnalysisRunsService.cancel(this.runId);
        this.$emit('cancelled', { runId: this.runId });
      } catch (error) {
        const status = error?.response?.status;
        this.cancelError =
          status === 409
            ? 'The run is already persisting its results and can no longer be cancelled.'
            : 'Could not request the cancellation. Please try again.';
        console.error('[AnalysisProgress] Cancel request failed:', error);
      } finally {
        this.cancelling = false;
      }
    },
  },
};
</script>

<style scoped>
.analysis-progress-card {
  border-left: 4px solid #6200ea;
}

.analysis-progress-card.is-cancelling {
  border-left-color: #fb8c00;
}

.current-step-text {
  font-size: 12px;
  color: #333;
  display: flex;
  align-items: center;
}
</style>
